import { useState } from 'react'
import { AlertIcon, CheckIcon } from '../components/icons'
import { JobIcon } from '../components/JobIcon'
import { TyreAxleQuantities } from '../components/TyreAxleQuantities'
import { TyreFitmentPicker } from '../components/TyreFitmentPicker'
import { TyreSizeEntry } from '../components/TyreSizeEntry'
import { formatMoney } from '../lib/format'
import {
  clampQuantity,
  describePosition,
  MAX_TYRE_QUANTITY,
  totalTyres,
  type TyreSelection,
  type TyreSizeChoice,
} from '../lib/tyres'
import type { TyreFitment } from '../types/PublicBookingChannelTypes'

export interface TyreStepProps {
  /**
   * Sizes from the registration lookup. Only a car the garage already holds
   * has any; for every other car this is empty and the customer types the size.
   */
  vehicleSizes: TyreSizeChoice[]
  /**
   * The car's factory fitments, standard first (ACA-98). When there are any
   * they are the choices, ahead of any sizes the garage holds.
   */
  fitments?: TyreFitment[]
  selection: TyreSelection | null
  onSelectionChange: (selection: TyreSelection | null) => void
  /** Price of fitting one tyre, from the garage's own tyre services. */
  fittingPrice: number | null
  showPrices: boolean
  vehicleLabel: string | null
}

/**
 * Tyre size and quantity.
 *
 * Sizes come from the registration where we know them, because reading a
 * sidewall in a car park is a genuine reason to abandon a booking: the car's
 * factory fitments first, then sizes the garage holds for it. Manual entry is
 * always available: aftermarket wheels, imports and plates we can't resolve
 * all end up there, and it validates the input so the fitter doesn't receive
 * "dunno, the normal ones".
 */
export function TyreStep({
  vehicleSizes,
  fitments = [],
  selection,
  onSelectionChange,
  fittingPrice,
  showPrices,
  vehicleLabel,
}: TyreStepProps) {
  const [manualEntry, setManualEntry] = useState(selection?.size.source === 'manual')

  // Two, not four. Pairs are the common purchase, one axle at a time, and
  // defaulting to a full set inflates the first price a customer sees by double.
  const quantity = selection ? clampQuantity(selection.quantity) : 2

  const chooseSize = (size: TyreSizeChoice) => {
    onSelectionChange({ size, quantity })
  }

  const hasFitments = fitments.length > 0
  const hasVehicleSizes = !hasFitments && vehicleSizes.length > 0
  const hasChoices = hasFitments || hasVehicleSizes
  const tyreCount = selection ? totalTyres(selection) : 0

  const startTyping = () => {
    setManualEntry(true)
    onSelectionChange(null)
  }

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">
          <JobIcon name="tyres" size={14} /> Tyres
        </p>
        <h3 className="ac-heading">
          {(hasFitments && !manualEntry) || hasVehicleSizes
            ? 'Which size do you need?'
            : 'What size are your tyres?'}
        </h3>
        <p className="ac-prose">
          {hasFitments && !manualEntry
            ? `These are the sizes ${vehicleLabel ?? 'your vehicle'} was made with.`
            : hasVehicleSizes
              ? `These are the sizes recorded for ${vehicleLabel ?? 'your vehicle'}.`
              : 'Enter the size from the side of your tyre, like 205/55R16.'}
        </p>
      </div>

      {hasFitments && !manualEntry ? (
        <TyreFitmentPicker
          fitments={fitments}
          selection={selection}
          onSelectionChange={onSelectionChange}
          onOtherSize={startTyping}
        />
      ) : null}

      {hasVehicleSizes ? (
        <ul className="ac-list">
          {vehicleSizes.map((size) => {
            const selected = !manualEntry && selection?.size.size === size.size
            return (
              <li key={size.size}>
                <button
                  type="button"
                  className={`ac-option${selected ? ' ac-option--selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => {
                    setManualEntry(false)
                    chooseSize(size)
                  }}
                >
                  <span className="ac-option__check" aria-hidden="true">
                    <CheckIcon size={15} />
                  </span>
                  <span className="ac-option__main">
                    <span className="ac-option__name">{size.size}</span>
                    <span className="ac-option__desc">{describePosition(size)}</span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}

      {hasVehicleSizes && !manualEntry ? (
        <button type="button" className="ac-btn ac-btn--ghost" onClick={startTyping}>
          My tyres are a different size
        </button>
      ) : null}

      {hasFitments && manualEntry ? (
        <button
          type="button"
          className="ac-btn ac-btn--ghost"
          onClick={() => {
            setManualEntry(false)
            onSelectionChange(null)
          }}
        >
          Show the sizes for my car
        </button>
      ) : null}

      {!hasChoices || manualEntry ? (
        <TyreSizeEntry selection={selection} onSelectionChange={onSelectionChange} numericKeyboard />
      ) : null}

      {selection ? (
        <div className="ac-stack ac-stack--tight">
          {selection.rear ? (
            <TyreAxleQuantities selection={selection} onSelectionChange={onSelectionChange} />
          ) : (
            <>
              <p className="ac-label">How many?</p>
              <div className="ac-quantity" role="group" aria-label="Number of tyres">
                {Array.from({ length: MAX_TYRE_QUANTITY }, (_, index) => index + 1).map((count) => {
                  const selected = quantity === count
                  return (
                    <button
                      key={count}
                      type="button"
                      className={`ac-quantity__option${selected ? ' ac-quantity__option--selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => onSelectionChange({ ...selection, quantity: count })}
                    >
                      {count}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {showPrices && fittingPrice != null ? (
            <div className="ac-notice ac-notice--info">
              <AlertIcon size={18} />
              <span>
                Fitting is {formatMoney(fittingPrice)} per tyre
                {fittingPrice > 0 && tyreCount > 0
                  ? `, ${formatMoney(fittingPrice * tyreCount)} for ${tyreCount}`
                  : ''}
                . We&apos;ll confirm the price of the tyres themselves before any work starts.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
